'use client';

import React from 'react';
import { Zap, Wrench, Bot } from 'lucide-react';
import type { PostCardProps } from './post-card';
import { stripMarkdown } from '@/lib/community/utils';
import { SmartCoverImage } from './smart-cover-image';
import { PostCardStats } from './post-card-stats';

export const PostCardAgent = React.memo(function PostCardAgent({ post, onClick, onTagClick, onLike, onFavorite }: PostCardProps) {
	return (
		<div
			className="liquid-glass-card rounded-md overflow-hidden cursor-pointer"
			onClick={(e) => {
				const card = e.currentTarget;
				const cover = card.querySelector('[data-cover]');
				onClick?.(post, card.getBoundingClientRect(), cover?.getBoundingClientRect() ?? undefined);
			}}
		>
			{post.coverImage && (
				<div data-cover>
					<SmartCoverImage alt={post.title} src={post.coverImage} />
				</div>
			)}

			<div className="p-3 flex flex-col gap-2">
				<h3 className="font-medium text-sm leading-snug truncate">{post.title}</h3>

				<p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{stripMarkdown(post.description)}</p>

				{post.agentDetail && (
					<div className="flex items-start gap-1.5 rounded-md border border-blue-700/25 bg-blue-700/10 px-3 py-2 w-fit">
						<Bot className="w-3.5 h-3.5 text-blue-700 dark:text-blue-400 shrink-0 mt-0.5" />
						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-2 mb-1.5">
								<span className="text-[10px] font-medium text-foreground">{post.agentDetail.role}</span>
								<div className="flex items-center gap-2 text-[10px] text-muted-foreground">
									<span className="inline-flex items-center gap-0.5">
										<Zap className="h-2.5 w-2.5" />
										{post.agentDetail.skillNames.length}
									</span>
									<span className="inline-flex items-center gap-0.5">
										<Wrench className="h-2.5 w-2.5" />
										{post.agentDetail.toolCount}
									</span>
								</div>
							</div>
							{post.agentDetail.skillNames.length > 0 && (
								<div className="flex flex-wrap items-center gap-1">
									{post.agentDetail.skillNames.slice(0, 3).map((skill) => (
										<span
											className="text-[9px] px-1.5 py-0.5 rounded-md bg-blue-700/10 text-blue-700/70 dark:text-blue-400/60 border border-blue-700/15"
											key={skill}
										>
											{skill}
										</span>
									))}
									{post.agentDetail.skillNames.length > 3 && <span className="text-[9px] text-muted-foreground/50">+{post.agentDetail.skillNames.length - 3}</span>}
								</div>
							)}
						</div>
					</div>
				)}

				{post.tags.length > 0 && (
					<div className="flex flex-wrap gap-1">
						{post.tags.slice(0, 3).map((tag) => (
							<button
								className="inline-block rounded-full bg-secondary/70 text-foreground/70 px-2 py-0.5 text-[10px] hover:text-foreground hover:bg-secondary transition-colors cursor-pointer"
								key={tag}
								onClick={(e) => {
									e.stopPropagation();
									onTagClick?.(tag);
								}}
								type="button"
							>
								#{tag}
							</button>
						))}
					</div>
				)}

				<PostCardStats onFavorite={onFavorite} onLike={onLike} post={post} />
			</div>
		</div>
	);
});
